/**
 * Canonical storey identity for design-fact paths.
 * One token per physical plan — not sheet notes, not area-table row indexes.
 */

import type { AiStoreyKind } from '../models/storey-level.model';

const PLAN_NUMBERS_RE =
  /(?:plan|våning|vaning|vån|etage|storey|floor)\s*(0*\d{1,2})(?![\d]|[:.,]\d)((?:\s*(?:[–—-]|,|&|och|till)\s*0*\d{1,2}(?![\d]|[:.,]\d))*)/i;
const PLAN_NUMBER_CONTINUATION_RE = /\s*([–—-]|,|&|och|till)\s*(0*\d{1,2})/gi;
const CANONICAL_PLAN_TOKEN_RE = /^plan_(\d{1,2}(?:[-+]\d{1,2})*)$/;
const CANONICAL_INDEX_TOKEN_RE = /^i_(-?\d{1,2})$/;

/** Section height lists use 0-based indexes; junk area-table rows sit above this. */
const MAX_INDEX_TOKEN = 24;

/** A multi-plan label longer than this is sheet prose, not a storey name. */
const MAX_MULTI_PLAN_LABEL_WORDS = 4;

const MAX_PLAN_RANGE_SPAN = 30;

/** Storey kinds a printed label names without a plan number. */
export type LabelledStoreyKind = Exclude<AiStoreyKind, 'regular'>;

const LABELLED_STOREY_KIND_PATTERNS: readonly (readonly [
  LabelledStoreyKind,
  RegExp,
])[] = [
  ['roof', /takplan/i],
  ['attic', /\bvind(?:s(?:plan|våning|vaning))?(?![a-zåäö])/i],
  [
    'technical',
    /teknik(?:våning|vaning|plan)|fläktrumsplan|fläktvåning|installationsvåning/i,
  ],
  ['split_level', /s[ou]uterr[äa]ng|suterr[äa]ng/i],
  ['typical', /normal(?:plan|våning|vaning)|typ(?:plan|våning|vaning)/i],
];

const ENTRANCE_TOKEN = 'entrance';

const UNDERGROUND_LABEL_RE =
  /\b(?:garage(?:plan|v[aå]ning|vaning)|k[aä]llar(?:e|plan|v[aå]ning|vaning)|kallar(?:e|plan))\b/i;

const GROUND_FLOOR_LABEL_RE =
  /\b(?:entr[eé]plan|botten(?:plan|v[aå]ning)|markplan)\b/i;

/** The whole label is an entrance name, not a sheet title that mentions one. */
const PRINTED_ENTRANCE_LABEL_RE =
  /^(?:entr[eé]plan|botten(?:plan|v[aå]ning)|markplan)$/i;

/** The sheet says this plan's form or use is not the one that repeats. */
const FORM_OR_USE_DIFFERS_RE =
  /avvik|skiljer sig|annan (?:utformning|anv[aä]ndning|planform|verksamhet)|olika (?:utformning|anv[aä]ndning|planform|verksamhet|planl[oö]sning)/i;

/** Apartment-type rows (`1 RoK`) are not storeys, even when the label says normalplan. */
const APARTMENT_TYPE_LABEL_RE = /\d+\s*rok\b/i;

/**
 * Entrance plan number when neither the model nor the sheet names one.
 * Not one below the next numbered sheet.
 */
export const UNSPECIFIED_ENTRANCE_PLAN_NUMBER = 10;

/** True when a label names an apartment type rather than a floor. */
export const isApartmentTypeLabel = (
  label: string | null | undefined,
): boolean => {
  if (label == null) return false;
  return APARTMENT_TYPE_LABEL_RE.test(label);
};

const STOREY_LABEL_KIND_TOKENS: readonly string[] = [
  ...LABELLED_STOREY_KIND_PATTERNS.map(([kind]) => kind),
  ENTRANCE_TOKEN,
];

const isStoreyLabelKindToken = (token: string): token is StoreyLabelKind =>
  STOREY_LABEL_KIND_TOKENS.includes(token);

const labelKindKey = (kind: StoreyLabelKind): CanonicalStoreyKey => ({
  token: kind,
  label: null,
  planNumbers: [],
  index: null,
  labelledKind: kind,
});

/** What a label names besides plan numbers; `entrance` for entréplan/bottenvåning. */
export type StoreyLabelKind = LabelledStoreyKind | 'entrance';

/** A labelled level, plus a garage or cellar under the entrance. */
export type MentionedStoreyKind = StoreyLabelKind | 'underground';

export type MentionedStorey = {
  kind: MentionedStoreyKind;
  /** The words in the label that named this level. */
  label: string;
};

const MENTIONED_STOREY_PATTERNS: readonly (readonly [
  MentionedStoreyKind,
  RegExp,
])[] = [
  ...LABELLED_STOREY_KIND_PATTERNS,
  ['entrance', GROUND_FLOOR_LABEL_RE],
  ['underground', UNDERGROUND_LABEL_RE],
];

export type CanonicalStoreyKey = {
  /** Path token: `plan_13`, `plan_11-14`, `attic`, `typical`, `entrance`, or `i_0`. */
  token: string;
  /** Display label (`Plan 13`, `Plan 11–14`) when the token is plan numbers. */
  label: string | null;
  /** Printed plan numbers, ascending. Empty for index and kind tokens. */
  planNumbers: number[];
  /** Bare 0-based position for `i_N` tokens. */
  index: number | null;
  /** Special, repeating or entrance storey the label names (`Vindsplan` → attic). */
  labelledKind: StoreyLabelKind | null;
};

const hasLeadingZero = (token: string): boolean =>
  token.startsWith('0') && token.length > 1;

/** Inclusive range. A printed leading zero is kept on every number in it. */
const expandPrintedRange = (from: string, to: string): string[] => {
  const start = Number(from);
  const end = Number(to);
  const pad = hasLeadingZero(from) || hasLeadingZero(to);
  const width = Math.max(from.length, to.length);
  const out: string[] = [];
  for (let n = start; n <= end; n++) {
    const text = String(n);
    out.push(pad ? text.padStart(width, '0') : text);
  }
  return out;
};

/**
 * Plan-number digits as printed, in reading order. Ranges expand
 * (`Plan 01-03` → `01`…`03`). A leading zero stays on the token.
 * Scales, years and descending tails are not plan numbers.
 */
export const parsePrintedPlanNumbers = (
  label: string | null | undefined,
): string[] => {
  if (label == null) return [];
  const match = PLAN_NUMBERS_RE.exec(label);
  const first = match?.[1];
  if (match == null || first === undefined) return [];
  const printed = [first];
  const numbers = [Number(first)];
  for (const part of (match[2] ?? '').matchAll(PLAN_NUMBER_CONTINUATION_RE)) {
    const separator = part[1];
    const nextPrinted = part[2];
    if (nextPrinted === undefined) break;
    const next = Number(nextPrinted);
    const previous = numbers[numbers.length - 1];
    const previousPrinted = printed[printed.length - 1];
    if (
      previous === undefined ||
      previousPrinted === undefined ||
      !(next > previous)
    )
      break;
    const isRange = separator !== undefined && /[–—-]|till/i.test(separator);
    if (isRange && next - previous > MAX_PLAN_RANGE_SPAN) break;
    if (!isRange) {
      printed.push(nextPrinted);
      numbers.push(next);
      continue;
    }
    for (const token of expandPrintedRange(previousPrinted, nextPrinted).slice(
      1,
    )) {
      printed.push(token);
      numbers.push(Number(token));
    }
  }
  return printed;
};

/**
 * Printed plan numbers from a Swedish/English floor label, ascending.
 * Ranges and lists expand (`Plan 11-14` → 11…14, `Plan 01, 02` → 1, 2).
 * Scales (`1:500`), years and descending tails (`Plan 11, 1 tr`) are not
 * plan numbers. Empty when the label prints none. The numeric value drops
 * a leading zero; {@link parsePrintedPlanNumbers} keeps the digits.
 */
export const parsePlanNumbers = (label: string | null | undefined): number[] =>
  parsePrintedPlanNumbers(label).map(Number);

/**
 * Single plan number from a Swedish/English floor label.
 * Ranges (`plan 13-19`) and sheet prose without a plan word return null.
 */
export const parsePlanNumber = (
  label: string | null | undefined,
): number | null => {
  const numbers = parsePlanNumbers(label);
  return numbers.length === 1 ? (numbers[0] ?? null) : null;
};

/**
 * Special or repeating storey a label names: takplan, vind, suterräng,
 * normalplan/typplan. Null for ordinary labels. A space on a floor is not a
 * kind. `Vindfång` is not an attic.
 */
export const labelledStoreyKind = (
  label: string | null | undefined,
): LabelledStoreyKind | null => {
  if (label == null) return null;
  for (const [kind, pattern] of LABELLED_STOREY_KIND_PATTERNS) {
    if (pattern.test(label)) return kind;
  }
  return null;
};

const isContiguous = (numbers: readonly number[]): boolean =>
  numbers.every((n, i) => i === 0 || n === (numbers[i - 1] ?? n) + 1);

const planTokenFromPrinted = (printed: readonly string[]): string => {
  const first = printed[0];
  const last = printed[printed.length - 1];
  if (printed.length === 1 || first === undefined || last === undefined) {
    return `plan_${printed.join('+')}`;
  }
  return isContiguous(printed.map(Number))
    ? `plan_${first}-${last}`
    : `plan_${printed.join('+')}`;
};

const formatPrintedPlanNumbers = (printed: readonly string[]): string => {
  const first = printed[0];
  const last = printed[printed.length - 1];
  if (first === undefined || last === undefined) return 'Plan';
  if (printed.length === 1) return `Plan ${first}`;
  return isContiguous(printed.map(Number))
    ? `Plan ${first}–${last}`
    : `Plan ${printed.join(', ')}`;
};

/** Display label from integers: `Plan 13`, `Plan 11–14`, `Plan 9, 11`. */
export const formatPlanNumbers = (numbers: readonly number[]): string =>
  formatPrintedPlanNumbers(numbers.map(String));

/**
 * Storey label for plan numbers. Digits already printed on `source` stay,
 * including a leading zero (`Plan 01` stays `Plan 01`). Integers alone
 * have no width to keep.
 */
export const planLabel = (
  numbers: readonly number[],
  source?: string | null,
): string => {
  const printed = source == null ? [] : parsePrintedPlanNumbers(source);
  if (
    printed.length === numbers.length &&
    printed.every((token, index) => Number(token) === numbers[index])
  ) {
    return formatPrintedPlanNumbers(printed);
  }
  const only = numbers.length === 1 ? numbers[0] : undefined;
  if (only !== undefined) {
    const token = printed.find((item) => Number(item) === only);
    if (token !== undefined) return `Plan ${token}`;
  }
  return formatPlanNumbers(numbers);
};

/**
 * Entrance name printed on its own (`Entréplan`, `Bottenplan`).
 * Null when the sheet says Plan N, or the text is a longer title.
 */
export const printedEntranceLabel = (
  label: string | null | undefined,
): string | null => {
  const trimmed = label?.trim() ?? '';
  if (!PRINTED_ENTRANCE_LABEL_RE.test(trimmed)) return null;
  return trimmed;
};

/**
 * Storey row label. A printed entrance name stays. {@link planLabel} keeps
 * the digits the row already printed (`Plan 01`). Any other written name is
 * kept when the row has no plan number.
 */
export const storeyRowLabel = (
  storey: { label: string | null; planNumbers: readonly number[] },
  fallback: string,
): string => {
  const entrance = printedEntranceLabel(storey.label);
  if (entrance != null) return entrance;
  if (storey.planNumbers.length > 0)
    return planLabel(storey.planNumbers, storey.label);
  const label = storey.label?.trim();
  if (label) return label;
  return fallback;
};

const planKey = (
  printed: readonly string[],
  labelledKind: LabelledStoreyKind | null,
): CanonicalStoreyKey => ({
  token: planTokenFromPrinted(printed),
  label: formatPrintedPlanNumbers(printed),
  planNumbers: printed.map(Number),
  index: null,
  labelledKind,
});

const printedFromTokenBody = (body: string): string[] => {
  const range = /^(\d{1,2})-(\d{1,2})$/.exec(body);
  if (range?.[1] !== undefined && range[2] !== undefined) {
    return expandPrintedRange(range[1], range[2]);
  }
  return body.split('+');
};

/**
 * Path token for a storey index the model already assigned.
 * 0 is the entrance storey. The printed name is not part of the token.
 */
export const storeyIndexKey = (index: number): CanonicalStoreyKey | null => {
  if (!Number.isInteger(index)) return null;
  if (index < -MAX_INDEX_TOKEN || index > MAX_INDEX_TOKEN) return null;
  return {
    token: `i_${index}`,
    label: null,
    planNumbers: [],
    index,
    labelledKind: null,
  };
};

export const parseCanonicalStoreyToken = (
  token: string,
): CanonicalStoreyKey | null => {
  const plan = CANONICAL_PLAN_TOKEN_RE.exec(token);
  if (plan?.[1] !== undefined)
    return planKey(printedFromTokenBody(plan[1]), null);
  const indexMatch = CANONICAL_INDEX_TOKEN_RE.exec(token);
  if (indexMatch?.[1] !== undefined) {
    const index = Number(indexMatch[1]);
    return {
      token: `i_${index}`,
      label: null,
      planNumbers: [],
      index,
      labelledKind: null,
    };
  }
  if (isStoreyLabelKindToken(token)) return labelKindKey(token);
  return null;
};

const wordCount = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;

/**
 * Resolve a storey path token. Printed plan numbers win, then a labelled
 * special storey (`Vindsplan` → `attic`, `Normalplan` → `typical`), then a
 * named entrance (`Entréplan` → `entrance`). Bare indexes only when they look
 * like 0-based storey positions (section height lists), never as area-table
 * row numbers. A negative index is a storey below ground whatever its
 * label (`Våning -2`), as area-table rows are never negative. Multi-plan sheet prose
 * (`Arearitning med planvyer för Plan 09–19`) is not a storey.
 */
export const canonicalStoreyKey = (input: {
  label?: string | null;
  index?: number | null;
}): CanonicalStoreyKey | null => {
  const label = input.label?.trim() ?? '';
  if (isApartmentTypeLabel(label)) return null;
  const kind = labelledStoreyKind(label);
  const printed = parsePrintedPlanNumbers(label);
  if (printed.length === 1) return planKey(printed, kind);
  if (printed.length > 1 && wordCount(label) <= MAX_MULTI_PLAN_LABEL_WORDS) {
    return planKey(printed, kind);
  }
  if (kind !== null) return labelKindKey(kind);
  if (labelLooksLikeGroundFloor(label) && wordCount(label) <= 2) {
    return labelKindKey(ENTRANCE_TOKEN);
  }
  const index = input.index;
  if (index == null) return null;
  if (index < 0) return storeyIndexKey(index);
  if (index > MAX_INDEX_TOKEN || label !== '') return null;
  return storeyIndexKey(index);
};

export const uniquePlanNumbersFromStoreyTokens = (
  tokens: readonly string[],
): number[] => {
  const plans = new Set<number>();
  for (const token of tokens) {
    for (const n of parseCanonicalStoreyToken(token)?.planNumbers ?? []) {
      plans.add(n);
    }
  }
  return [...plans].sort((a, b) => a - b);
};

const ELEVATION_MARK_RE = /\(\s*([+＋-]?)\s*(\d+(?:[.,]\d+)?)\s*\)/;

/**
 * Printed elevation stamp (`Plan 13 (+20)` → 20). Not a storey index.
 */
export const parseElevationMark = (
  label: string | null | undefined,
): number | null => {
  if (label == null) return null;
  const match = ELEVATION_MARK_RE.exec(label);
  if (match?.[2] === undefined) return null;
  const n = Number(match[2].replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return match[1] === '-' ? -n : n;
};

export const labelLooksLikeGroundFloor = (
  label: string | null | undefined,
): boolean => {
  if (label == null) return false;
  return GROUND_FLOOR_LABEL_RE.test(label);
};

/**
 * Garage or cellar printed as its own level (`garageplan`, `källarplan`).
 * A program named "garage" on an ordinary floor is not an underground storey.
 */
export const labelLooksLikeUnderground = (
  label: string | null | undefined,
): boolean => {
  if (label == null) return false;
  return UNDERGROUND_LABEL_RE.test(label);
};

/** True when the label says the plan form or the use differs from the repeated plan. */
export const labelSaysFormOrUseDiffers = (
  label: string | null | undefined,
): boolean => label != null && FORM_OR_USE_DIFFERS_RE.test(label);

/**
 * Every level a label names, in reading order. One kind once.
 * `entréplan, typplan och garageplan` is entrance, then typical, then underground.
 */
export const mentionedStoreys = (
  label: string | null | undefined,
): MentionedStorey[] => {
  if (label == null || label.trim() === '') return [];
  const hits = MENTIONED_STOREY_PATTERNS.flatMap(([kind, pattern]) => {
    const match = label.match(pattern);
    if (match?.index === undefined || match[0] === undefined) return [];
    return [{ kind, label: match[0], index: match.index }];
  });
  hits.sort((a, b) => a.index - b.index);
  const seen = new Set<MentionedStoreyKind>();
  return hits.flatMap((hit) => {
    if (seen.has(hit.kind)) return [];
    seen.add(hit.kind);
    return [{ kind: hit.kind, label: hit.label }];
  });
};

/**
 * Longest contiguous run of plan numbers. Drops isolated outliers
 * (`Plan 1` next to Plan 09–19).
 */
export const longestContiguousPlanCluster = (
  plans: readonly number[],
): number[] => {
  const unique = [...new Set(plans)].sort((a, b) => a - b);
  const head = unique[0];
  if (head === undefined) return [];
  let best: number[] = [head];
  let current: number[] = [head];
  for (let i = 1; i < unique.length; i++) {
    const prev = unique[i - 1];
    const next = unique[i];
    if (prev === undefined || next === undefined) continue;
    if (next === prev + 1) {
      current = [...current, next];
      continue;
    }
    if (current.length > best.length) best = current;
    current = [next];
  }
  return current.length > best.length ? current : best;
};

/**
 * Ground plan comes from a ground-floor label, or exactly one storey the
 * model marked as the entrance. The mark may be the model's guess of the
 * numbering system.
 */
export const inferGroundFloorPlanNumber = (
  storeys: readonly {
    label: string | null;
    planNumber: number | null;
    markedEntrance: boolean;
  }[],
): number | null => {
  for (const storey of storeys) {
    if (labelLooksLikeGroundFloor(storey.label) && storey.planNumber != null) {
      return storey.planNumber;
    }
  }
  const marked = [
    ...new Set(
      storeys.flatMap((storey) =>
        storey.markedEntrance && storey.planNumber != null
          ? [storey.planNumber]
          : [],
      ),
    ),
  ];
  if (marked.length !== 1) return null;
  return marked[0] ?? null;
};
